import hasEffectiveConsent from '../util/consent/effectiveConsent';
import { logError } from '../util/log';
import { hasNativeAnalytics, nativeLogEvent, nativeSetUserId, nativeSetUserProperties } from './nativeFirebase';

export class FirebaseBaseEvent {
  static params = {};
  static name = '';

  static setParam(key, value) {
    if (value) this.params[key] = value;
    return this;
  }

  static async logEvent() {
    // Snapshot name/params and reset BEFORE any await — the params live on
    // mutable static class state, so a concurrent event builder would
    // otherwise interleave with the async consent check / SDK import.
    const name = this.name;
    const params = this.params;
    this.params = {};
    if (typeof window === 'undefined') return;
    if (hasNativeAnalytics()) {
      // Cordova app: log through the native SDK (app data stream) instead of
      // the web JS SDK, so GA4 reports platform = Android/iOS. The native SDK
      // has no consent mode, so statistics consent stays a hard gate there.
      const permission = await hasEffectiveConsent('statistics');
      if (!permission) return;
      await nativeLogEvent(name, params);
      return;
    }
    // Web: no consent gate — advanced consent mode. gtag decides per hit what
    // it may store/send from the consent signals; a denied hit is still sent
    // cookieless. googleAnalytics only exists in production (see applyConsent).
    if (!window.googleAnalytics) return;
    const { logEvent } = await import('firebase/analytics');
    logEvent(window.googleAnalytics, name, params);
  }

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withValue(value) {
    return this.setParam('value', value);
  }

  static withCoupon(coupon) {
    return this.setParam('coupon', coupon);
  }

  static withItems(items) {
    return this.setParam('items', items);
  }

  static withContents(contents) {
    return this.setParam('contents', contents);
  }

  static withNumItems(numItems) {
    return this.setParam('num_items', numItems);
  }

  static withPageReferer(pageReferer) {
    return this.setParam('page_referer', pageReferer);
  }

  static withMethod(method) {
    return this.setParam('method', method);
  }

  static withItemId(itemId) {
    return this.setParam('item_id', itemId);
  }

  static withAdditionalKeys(additionalKeys) {
    additionalKeys.forEach(({ key, value }) => {
      this.setParam(key, value);
    });
    return this;
  }
}

export class FirebaseScrollEvent extends FirebaseBaseEvent {
  static name = 'scroll';
}

export class FirebasePageViewEvent extends FirebaseBaseEvent {
  static name = 'page_view';

  static withPageLocation(pageLocation) {
    return this.setParam('page_location', pageLocation);
  }

  static withPageReferer(pageReferer) {
    return this.setParam('page_referer', pageReferer);
  }
}

// App-stream equivalent of page_view: GA4 app properties report screens, not
// pages. Only logged on the native (Cordova) path.
export class FirebaseScreenViewEvent extends FirebaseBaseEvent {
  static name = 'screen_view';

  static withScreenName(screenName) {
    return this.setParam('screen_name', screenName);
  }

  static withPageLocation(pageLocation) {
    return this.setParam('page_location', pageLocation);
  }

  static withPageReferer(pageReferer) {
    return this.setParam('page_referer', pageReferer);
  }
}

export class FirebaseLinkClickEvent extends FirebaseBaseEvent {
  static name = 'click';

  static withLinkClasses(linkClasses) {
    return this.setParam('link_classes', linkClasses);
  }

  static withLinkDomain(linkDomain) {
    return this.setParam('link_domain', linkDomain);
  }

  static withLinkId(linkId) {
    return this.setParam('link_id', linkId);
  }

  static setOutbound(outbound) {
    return this.setParam('outbound', outbound);
  }
}

export class FirebaseViewSearchResultsEvent extends FirebaseBaseEvent {
  static name = 'view_search_results';

  static withSearchTerm(searchTerm) {
    return this.setParam('search_term', searchTerm);
  }

  static withAdditionalKeys(additionalKeys) {
    additionalKeys.forEach(({ key, value }) => {
      this.setParam(key, value);
    });
    return this;
  }
}

export class FirebaseLoginEvent extends FirebaseBaseEvent {
  static name = 'login';

  static withLoginMethod(loginMethod) {
    return this.setParam('login_method', loginMethod);
  }
}

export class FirebaseSignUpEvent extends FirebaseBaseEvent {
  static name = 'sign_up';

  static withSignUpMethod(signUpMethod) {
    return this.setParam('sign_up_method', signUpMethod);
  }
}

export class FirebasePurchaseEvent extends FirebaseBaseEvent {
  static name = 'purchase';

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withTransactionId(transactionId) {
    return this.setParam('transaction_id', transactionId);
  }

  static withValue(value) {
    return this.setParam('value', value);
  }

  static withCoupon(coupon) {
    return this.setParam('coupon', coupon);
  }

  static withItems(items) {
    return this.setParam('items', items);
  }
}

export class FirebaseRefundEvent extends FirebasePurchaseEvent {
  static name = 'refund';
}

export class FirebaseShareEvent extends FirebaseBaseEvent {
  static name = 'share';

  static withContentType(contentType) {
    return this.setParam('content_type', contentType);
  }

  static withMethod(method) {
    return this.setParam('method', method);
  }

  static withItemId(itemId) {
    return this.setParam('item_id', itemId);
  }
}

export class FirebaseAddPaymentInfoEvent extends FirebaseBaseEvent {
  static name = 'add_payment_info';

  static withPaymentInfo(paymentInfo) {
    return this.setParam('payment_info', paymentInfo);
  }
}

export class FirebaseAddToWishlistEvent extends FirebaseBaseEvent {
  static name = 'add_to_wishlist';

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withValue(value) {
    return this.setParam('value', value);
  }

  static withItems(items) {
    return this.setParam('items', items);
  }
}

export class FirebaseBeginCheckoutEvent extends FirebaseBaseEvent {
  static name = 'begin_checkout';

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withValue(value) {
    return this.setParam('value', value);
  }

  static withCoupon(coupon) {
    return this.setParam('coupon', coupon);
  }

  static withItems(items) {
    return this.setParam('items', items);
  }
}

// Custom-Event für den Anfrage-Flow: ersetzt den zu frühen purchase als
// Optimierungs-Signal bei Preauth (Spec §3.1).
export class FirebaseBookingRequestEvent extends FirebaseBaseEvent {
  static name = 'booking_request';

  static withTransactionId(transactionId) {
    return this.setParam('transaction_id', transactionId);
  }
}

export class FirebaseViewItemEvent extends FirebaseBaseEvent {
  static name = 'view_item';

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withValue(value) {
    return this.setParam('value', value);
  }
}

export class FirebaseCustomEvent extends FirebaseBaseEvent {
  static name = 'custom';

  static withParams(params) {
    this.params = { ...this.params, ...params };
    return this;
  }

  static withCustomName(customName) {
    this.name = customName;
    return this;
  }
}

export class FirebaseOrderItem {
  constructor() {
    this.item_id = null;
    this.item_name = null;
    this.currency = null;
    this.item_category = '';
    this.item_category1 = '';
    this.item_category2 = '';
    this.item_category3 = '';
    this.item_category4 = '';
    this.item_category5 = '';
    this.price = null;
    this.quantity = null;
  }

  static withItemId(itemId) {
    if (itemId) this.item_id = itemId;
    return this;
  }

  // Unlike the other setters this assigns unconditionally: the builder keeps its
  // state on the class itself and make() does not reset it, so a falsy value has
  // to actively clear a title left over from the previously built item. Only the
  // stay line item carries an item_name — extras must come out without one.
  static withItemName(itemName) {
    this.item_name = itemName || null;
    return this;
  }

  static withCurrency(currency) {
    if (currency) this.currency = currency;
    return this;
  }

  static withItemCategory(itemCategory) {
    if (itemCategory) this.item_category = itemCategory;
    return this;
  }

  static withItemCategory1(itemCategory) {
    if (itemCategory) this.item_category1 = itemCategory;
    return this;
  }

  static withItemCategory2(itemCategory) {
    if (itemCategory) this.item_category2 = itemCategory;
    return this;
  }

  static withItemCategory3(itemCategory) {
    if (itemCategory) this.item_category3 = itemCategory;
    return this;
  }

  static withItemCategory4(itemCategory) {
    if (itemCategory) this.item_category4 = itemCategory;
    return this;
  }

  static withItemCategory5(itemCategory) {
    if (itemCategory) this.item_category5 = itemCategory;
    return this;
  }

  static withPrice(price) {
    if (price) this.price = price;
    return this;
  }

  static withQuantity(quantity) {
    if (quantity) {
      if (typeof quantity === 'object' && typeof quantity.toString === 'function') {
        this.quantity = parseInt(quantity.toString());
      } else {
        this.quantity = quantity;
      }
    }
    return this;
  }

  static make() {
    try {
      if (!this.item_id) {
        throw new Error('item_id is required');
      }
      if (!this.currency) {
        throw new Error('currency is required');
      }
      if (!this.price) {
        throw new Error('price is required');
      }
      if (!this.quantity) {
        throw new Error('quantity is required');
      }
      return {
        item_id: this.item_id,
        ...(this.item_name ? { item_name: this.item_name } : {}),
        currency: this.currency,
        item_category: this.item_category || '',
        item_category2: this.item_category2 || '',
        item_category3: this.item_category3 || '',
        item_category4: this.item_category4 || '',
        item_category5: this.item_category5 || '',
        price: this.price,
        quantity: this.quantity,
      };
    } catch (e) {
      logError(e, 'firebase-order-item-error');
    }
  }
}

export const setGoogleUserId = async (userId) => {
  const permission = await hasEffectiveConsent('statistics');
  if (!permission) return;
  if (typeof window === 'undefined') return;
  if (hasNativeAnalytics()) {
    await nativeSetUserId(userId);
    return;
  }
  if (!window.googleAnalytics) return;
  const { setUserId } = await import('firebase/analytics');
  setUserId(window.googleAnalytics, userId);
};

export const setGoogleUserProperties = async (params) => {
  const permission = await hasEffectiveConsent('statistics');
  if (!permission) return;
  if (typeof window === 'undefined') return;
  if (hasNativeAnalytics()) {
    await nativeSetUserProperties(params);
    return;
  }
  if (!window.googleAnalytics) return;
  const { setUserProperties } = await import('firebase/analytics');
  setUserProperties(window.googleAnalytics, params);
};